<!--
  Single inventory field card: header (grip, name, settings, delete), type + default row, flag checkboxes.
  When `readonly` (common / inherited fields), the name is plain text — not a VTextField.
-->
<script setup lang="ts">
import { computed } from 'vue'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { InventTypeField } from '@/entities/Inventory/TypeField'
import { VIcons } from '@/shared/ui'

const props = defineProps<{
  item: ObjectInventoryField
  readonly?: boolean
  showDragGrip?: boolean
  typeItems: { type?: string; title: string; value?: string }[]
}>()

/** Inherited fields stay read-only except `mandatory` (child-type override). */
const MANDATORY_EXCLUDED_TYPES = new Set(['computed', 'interator_simple', 'boolean'])

const canToggleMandatory = computed(
  () => !MANDATORY_EXCLUDED_TYPES.has(String(props.item.type ?? '')),
)

const emit = defineEmits<{
  editSettings: []
  delete: []
}>()
</script>

<template>
  <div class="pb-inv-card" :class="{ 'pb-inv-card--inherit': readonly }">
    <header class="pb-inv-card__hd">
      <div class="pb-inv-card__title">
        <span
          class="pb-inv-card__grip"
          :class="{ 'pb-inv-card__grip--hidden': !showDragGrip }"
          aria-hidden="true"
        >
          <VIcons type="more_vert" />
        </span>
        <VTextField
          v-if="!readonly"
          v-model="item.name"
          class="pb-inv-card__name"
          density="compact"
          variant="plain"
          :placeholder="$t('itf.new_field_name_placeholder')"
          hide-details
          single-line
          :aria-label="$t('itf.name_label')"
        />
        <div
          v-else
          class="pb-inv-card__name pb-inv-card__name-plain"
          :title="item.name"
        >
          {{ item.name || $t('itf.not_set') }}
        </div>
      </div>
      <div class="pb-inv-card__tools">
        <VBtn
          class="pb-inv-card__btn-ico"
          variant="outlined"
          size="small"
          :disabled="readonly"
          :title="$t('itf.type_field')"
          @click="emit('editSettings')"
        >
          <VIcons type="tune" />
        </VBtn>
        <VBtn
          v-if="!readonly"
          class="pb-inv-card__btn-del"
          variant="outlined"
          size="small"
          :aria-label="$t('itf.delete')"
          :title="$t('itf.delete')"
          @click="emit('delete')"
        >
          <VIcons type="delete" />
        </VBtn>
      </div>
    </header>

    <div class="pb-inv-card__body">
      <div class="pb-inv-card__row-type-value">
        <div class="pb-inv-card__fld">
          <VSelect
            v-model="item.type"
            :items="typeItems"
            density="compact"
            hide-details
            variant="outlined"
            class="pb-inv-card__sel"
            :readonly="readonly"
            :aria-label="$t('itf.type_field')"
          />
        </div>
        <div
          class="pb-inv-card__fld"
          :class="{ 'pb-inv-card__fld--bool-default': item.type === 'boolean' }"
        >
          <InventTypeField
            :field-data="item"
            v-bind="{ ...item, mandatory: false }"
            hide-details
            v-model="item.default_value"
            :disabled="readonly"
            :aria-label="$t('itf.default_value')"
          />
        </div>
      </div>

      <div class="pb-inv-card__flags" role="group" :aria-label="$t('itf.show_in_filter')">
        <label class="pb-inv-card__flag">
          <VCheckbox
            v-model="item.is_global_search"
            hide-details
            density="compact"
            :disabled="readonly"
          />
          <span>{{ $t('itf.field_flag_short_search') }}</span>
        </label>
        <label class="pb-inv-card__flag">
          <VCheckbox
            v-model="item.is_filtered"
            hide-details
            density="compact"
            :disabled="readonly"
          />
          <span>{{ $t('itf.field_flag_short_filter') }}</span>
        </label>
        <label class="pb-inv-card__flag">
          <VCheckbox
            v-model="item.is_table"
            hide-details
            density="compact"
            :disabled="readonly"
          />
          <span>{{ $t('itf.field_flag_short_table') }}</span>
        </label>
        <label class="pb-inv-card__flag">
          <VCheckbox
            v-model="item.is_system"
            hide-details
            density="compact"
            :disabled="readonly"
          />
          <span>{{ $t('itf.field_flag_short_system') }}</span>
        </label>
        <label
          v-if="canToggleMandatory"
          class="pb-inv-card__flag"
          :class="{ 'pb-inv-card__flag--mandatory-only': readonly }"
        >
          <VCheckbox
            v-model="item.mandatory"
            hide-details
            density="compact"
          />
          <span>{{ $t('itf.required') }}</span>
        </label>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$pb-hd-h: 28px;

.pb-inv-card {
  --pb-rail: linear-gradient(180deg, rgb(var(--v-theme-primary)) 0%, #6366f1 55%, #8b5cf6 100%);
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 6px 6px 8px 10px;
  border: 1px solid rgb(var(--v-theme-outline-variant));
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
  box-shadow:
    0 2px 10px rgba(14, 165, 233, 0.09),
    0 1px 2px rgba(12, 74, 110, 0.05);
}

.pb-inv-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 4px;
  border-radius: 0 3px 3px 0;
  background: var(--pb-rail);
}

.pb-inv-card--inherit {
  background: rgba(var(--v-theme-primary), 0.06);
  border-color: rgba(var(--v-theme-primary), 0.35);
}

.pb-inv-card__flag--mandatory-only {
  flex-basis: 100%;
  margin-top: 2px;
  padding-top: 4px;
  border-top: 1px dashed rgba(var(--v-theme-primary), 0.25);
}

.pb-inv-card__hd {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  margin-bottom: 5px;
  min-height: $pb-hd-h;
}

.pb-inv-card__title {
  display: flex;
  align-items: center;
  gap: 0;
  min-width: 0;
  flex: 1;
}

.pb-inv-card__grip {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: $pb-hd-h;
  height: $pb-hd-h;
  margin-left: -7px;
  color: rgba(var(--v-theme-primary), 0.55);
  cursor: grab;
}

.pb-inv-card__grip--hidden {
  visibility: hidden;
  pointer-events: none;
}

.pb-inv-card__name {
  flex: 1;
  min-width: 0;
  margin-left: -7px;
  font-size: 0.8125rem;
}

.pb-inv-card__name :deep(.v-field) {
  min-height: $pb-hd-h;
  padding-top: 0;
  padding-bottom: 0;
}

.pb-inv-card__name-plain {
  display: flex;
  align-items: center;
  min-height: $pb-hd-h;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgb(var(--v-theme-on-surface));
}

.pb-inv-card__tools {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  flex-wrap: nowrap;
}

.pb-inv-card__btn-ico {
  box-sizing: border-box;
  min-width: $pb-hd-h !important;
  width: $pb-hd-h;
  height: $pb-hd-h;
  padding: 0 !important;
}

.pb-inv-card__btn-del {
  box-sizing: border-box;
  min-width: $pb-hd-h !important;
  width: $pb-hd-h;
  height: $pb-hd-h !important;
  padding: 0 !important;
  border-color: rgba(var(--v-theme-error), 0.45) !important;
  color: rgb(var(--v-theme-error)) !important;
}

.pb-inv-card__body {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
}

.pb-inv-card__row-type-value {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 6px 8px;
  align-items: end;
}

@media (max-width: 360px) {
  .pb-inv-card__row-type-value {
    grid-template-columns: 1fr;
  }
}

.pb-inv-card__fld {
  margin-bottom: 0;
  min-width: 0;
}

/* Boolean default: VCheckbox centered in the grid cell */
.pb-inv-card__fld--bool-default {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pb-inv-card__fld--bool-default :deep(.v-input) {
  flex: 0 0 auto;
  width: auto;
  max-width: 100%;
}

.pb-inv-card__sel {
  width: 100%;
  min-width: 0;
}

.pb-inv-card__flags {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  margin: 4px 0 0;
  padding: 0;
  border: none;
}

.pb-inv-card__flag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
}

.pb-inv-card__flag :deep(.v-selection-control) {
  min-height: auto;
}
</style>
